/**
 * Phase 1b — Memory-optimization PR foundation: DIMVAR (Dimension Variable)
 * group code mapping table per the AutoCAD DXF Reference.
 *
 * Used by:
 *   - parser (`sectionParsers.ts`): translate XData group codes to typed
 *     `DimStyleOverrides` fields
 *   - writer (`writer.ts:1132`, `DIMVAR_NAME_TO_CODE`): serialize typed
 *     `DimStyleOverrides` back to DXF group codes
 *
 * Group codes are cross-referenced with the inline mapping in
 * `writer.ts:1132-1203` (single source of truth — must stay in sync).
 *
 * This module ships the 23 DIMVARs the Phase 1b spec calls out as
 * supported in `DimensionEntity.styleOverrides`. The remaining DIMVARs
 * (DIMSCALE, DIMTAD, DIMJUST, DIMSAH, DIMSE1/2 already here, …) live
 * in the wider DIMSTYLE table mapping and are out of scope for this
 * extraction (see writer.ts for the full set).
 */

export type DimVarKey =
  | "dimasz"
  | "dimtxt"
  | "dimgap"
  | "dimexo"
  | "dimexe"
  | "dimse1"
  | "dimse2"
  | "dimclrd"
  | "dimclre"
  | "dimdec"
  | "dimpost"
  | "dimblk"
  | "dimblk1"
  | "dimblk2"
  | "dimtih"
  | "dimrnd"
  | "dimlfac"
  | "dimlunit"
  | "dimtol"
  | "dimlim"
  | "dimtp"
  | "dimtm"
  | "dimtfac";

export interface DimVarDef {
  key: DimVarKey;
  /** DXF group code per AutoCAD DXF Reference. */
  groupCode: number;
  type: "number" | "int" | "boolean" | "string";
  defaultValue: number | string | boolean;
  /**
   * SL-11 — the DIMVAR is an ARROWHEAD-BLOCK reference (`dimblk` 342 / `dimblk1`
   * 343 / `dimblk2` 344). In a DIMENSION's per-entity `ACAD`/`DSTYLE` XDATA
   * override, these DIMSTYLE group codes are HANDLE codes (Autodesk DXF R2018
   * DSTYLE appendix; ezdxf `get_xcode_for` maps every DIMSTYLE code in
   * 320-369 → XDATA `1005` soft-pointer handle — only R12 code 5 stays `1000`).
   * The AUTHORED model value is the block NAME (`_OPEN30`, `""` = default
   * CLOSEDFILLED); an IMPORTED value is a soft-pointer HANDLE kept in the
   * `styleOverrides.dimblk*Handle` provenance twin (SL-11 round-3 #3/#4). The
   * export boundary emits a NAME as a `1000` placeholder (the stamp resolves it →
   * BLOCK_RECORD handle, empty → null handle `0`) and an imported HANDLE as
   * `1005` directly (already record-remap-followed). A `1000 <name>` (R12-shaped)
   * is dropped by AutoCAD/ezdxf on read (verified: ezdxf `get_acad_dstyle` reads a
   * handle code and ignores an unresolvable string). `type` stays `"string"`;
   * `blockRef` marks the arrowhead codes + triggers handle resolution.
   */
  blockRef?: boolean;
}

// Defaults match the AutoCAD "Standard" dimension style as documented in
// the Autodesk DXF reference. Cross-referenced with
// `packages/drawing-model/src/model.ts:754` (`dimensionStyleSchema`).
export const DIM_VAR_TABLE: readonly DimVarDef[] = [
  { key: "dimasz", groupCode: 41, type: "number", defaultValue: 2.5 },
  { key: "dimtxt", groupCode: 140, type: "number", defaultValue: 2.5 },
  { key: "dimgap", groupCode: 147, type: "number", defaultValue: 0.625 },
  { key: "dimexo", groupCode: 42, type: "number", defaultValue: 0.625 },
  { key: "dimexe", groupCode: 44, type: "number", defaultValue: 1.25 },
  { key: "dimse1", groupCode: 75, type: "boolean", defaultValue: false },
  { key: "dimse2", groupCode: 76, type: "boolean", defaultValue: false },
  { key: "dimclrd", groupCode: 176, type: "int", defaultValue: 0 },
  { key: "dimclre", groupCode: 177, type: "int", defaultValue: 0 },
  { key: "dimdec", groupCode: 271, type: "int", defaultValue: 4 },
  { key: "dimpost", groupCode: 3, type: "string", defaultValue: "" },
  // SL-11 — arrowhead-block DIMVARs: DSTYLE override value is a `1005` handle,
  // not a `1000` string. `blockRef: true` is the single source of truth.
  { key: "dimblk", groupCode: 342, type: "string", defaultValue: "", blockRef: true },
  { key: "dimblk1", groupCode: 343, type: "string", defaultValue: "", blockRef: true },
  { key: "dimblk2", groupCode: 344, type: "string", defaultValue: "", blockRef: true },
  { key: "dimtih", groupCode: 73, type: "boolean", defaultValue: true },
  { key: "dimrnd", groupCode: 45, type: "number", defaultValue: 0 },
  { key: "dimlfac", groupCode: 144, type: "number", defaultValue: 1 },
  { key: "dimlunit", groupCode: 277, type: "int", defaultValue: 2 },
  { key: "dimtol", groupCode: 71, type: "boolean", defaultValue: false },
  { key: "dimlim", groupCode: 72, type: "boolean", defaultValue: false },
  { key: "dimtp", groupCode: 47, type: "number", defaultValue: 0 },
  { key: "dimtm", groupCode: 48, type: "number", defaultValue: 0 },
  { key: "dimtfac", groupCode: 146, type: "number", defaultValue: 1 },
];

export const DIM_VAR_BY_KEY: Record<DimVarKey, DimVarDef> = Object.fromEntries(
  DIM_VAR_TABLE.map((d) => [d.key, d]),
) as Record<DimVarKey, DimVarDef>;

export const DIM_VAR_BY_GROUP_CODE: ReadonlyMap<number, DimVarDef> = new Map(
  DIM_VAR_TABLE.map((d) => [d.groupCode, d]),
);

/**
 * SL-11 — the DIMSTYLE group codes whose DSTYLE-override XDATA value is a
 * BLOCK_RECORD handle (`1005`), derived from `DIM_VAR_TABLE` so the marker has
 * exactly one source of truth. Consumed by the export handle-resolution stamp
 * (`stampDimensionDStyleArrowheadHandles`) which walks a DIMENSION's DSTYLE
 * XDATA and rewrites each `1070 <code>` arrowhead value → the resolved handle.
 */
export const DIM_ARROWHEAD_BLOCK_CODES: ReadonlySet<number> = new Set(
  DIM_VAR_TABLE.filter((d) => d.blockRef).map((d) => d.groupCode),
);
